import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"

import SectionTitle from "./SectionTitle"
import ScrollCarousel from "./ScrollCarousel"
import GenericCarouselWrapper from "./GenericCarouselWrapper"

import Section from "../atoms/Section"
import Box from "../atoms/Box"

import Reveal from "../utils/Reveal"

/**
 * A Generic Component that returns various styles of carousels for Morphic's grid sections
 * @param {Object} param0 - section level props for configuring and rendering carousel for all grid sections
 * @returns {React Component}
 */
const GenericCarousel = ({
  section,
  sectionTitle,
  carousel,
  logoOrImage = "reactChild",
  children,
  customImageSize,
  ...rest
}) => {
  const [carouselItems, setCarouselItems] = useState(
    React.Children.map(children, (child, i) => ({ child, key: i })),
  )

  useEffect(() => {
    setCarouselItems(
      React.Children.map(children, (child, i) => ({ child, key: i })),
    )
  }, [children])

  const scrollBehavior = carousel?.scroll
  const scrollSpeed = carousel?.carouselSpeed
  const itemSpacing = carousel?.spacing
  const carouselWidth = carousel?.carouselWidth || "normal"

  return (
    <Section {...section} noPx={true}>
      <GenericCarouselWrapper spacing={itemSpacing} size={customImageSize}>
        {sectionTitle && (
          <Reveal>
            <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
          </Reveal>
        )}
        <Box
          position="relative"
          width={carouselWidth === "normal" ? "100%" : "100vw"}
          ml={carouselWidth === "normal" ? undefined : "-50vw"}
          left={carouselWidth === "normal" ? undefined : "49.3%"}
        >
          {carouselItems?.length > 0 && scrollBehavior === "automatic" && (
            <ScrollCarousel
              carouselItems={carouselItems}
              logoOrImage={logoOrImage}
              customImageSize={300}
              customImageHeight={null}
              customSizeMobile={150}
              carouselSpeed={scrollSpeed}
              {...rest}
            />
          )}
          {carouselItems?.length > 0 && scrollBehavior === "manual" && (
            <GenericCarouselWrapper.Track>
              {carouselItems.map((item, i) => item.child)}
            </GenericCarouselWrapper.Track>
          )}
        </Box>
        <Box maxWidth="var(--container-width)" mx="auto">
          <GenericCarouselWrapper.Controls
            arrows={carousel.showArrows}
            indicators={carousel.showIndicators}
            indicatorsProps={{ controlsAlignment: carousel.controlsAlignment }}
            count={carouselItems.length}
          />
        </Box>
      </GenericCarouselWrapper>
    </Section>
  )
}

export default GenericCarousel

export const query = graphql`
  fragment GenericCarousel on STRAPI__COMPONENT_MOLECULES_GENERIC_CAROUSEL {
    useCarousel
    scroll
    showArrows
    showIndicators
    controlsLocation
    controlsAlignment
    spacing
    carouselSpeed
    carouselWidth
  }
`
